import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "folio_theme";
const PROMPTED_KEY = "folio_theme_prompted";

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "light";
    } catch (err) {
      console.warn("[theme] cannot read localStorage:", err);
      return "light";
    }
  });

  const [prompted, setPrompted] = useState(() => {
    try {
      return localStorage.getItem(PROMPTED_KEY) === "1";
    } catch (err) {
      console.warn("[theme] cannot read prompted flag:", err);
      return true;
    }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (err) {
      console.warn("[theme] cannot persist:", err);
    }
  }, [theme]);

  const setTheme = useCallback((t) => setThemeState(t), []);
  const toggle = useCallback(
    () => setThemeState((t) => (t === "light" ? "dark" : "light")),
    []
  );
  const markPrompted = useCallback(() => {
    try {
      localStorage.setItem(PROMPTED_KEY, "1");
    } catch (err) {
      console.warn("[theme] cannot persist prompted flag:", err);
    }
    setPrompted(true);
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, toggle, prompted, markPrompted }),
    [theme, setTheme, toggle, prompted, markPrompted]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
